import React, { useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { LandingSignalFlow, FocusStage } from '../00-shared/LandingSignalFlow';
import { LandingInsightNarrative } from '../00-shared/LandingInsightNarrative';
import {
  VIEW_W,
  SRC_CX, SIG_CX, INT_CX, INS_CX,
} from '../00-shared/landing-signal-flow-data';

// ScrollyHero is self-styled: it uses the landing notebook-grid + brand
// colors hardcoded in its subcomponents. No props are needed today; if a
// future variant needs theming, reintroduce them with real consumers.
interface ScrollyHeroProps {}

// ─── Stage narratives ──────────────────────────────────────────────────
const STAGES = [
  {
    num: '01', tag: 'SENSORS',
    title: 'Every device you already own.',
    body: 'Wearables, force plates, GPS, subjective check-ins, ingested from the systems your athletes already use. One schema, gap-aware, per-session resolution.',
    bullets: [
      'WHOOP · Garmin · Oura · Catapult · Hawkin · VALD · athlete wellness',
      'Per-athlete, per-session. No population averages.',
      'Missing data is surfaced, not silently imputed',
    ],
  },
  {
    num: '02', tag: 'SIGNALS',
    title: 'Derived features, not raw noise.',
    body: 'Raw HRV or sleep minutes don\u2019t tell you if someone is in trouble. We compute trends, z-scores, and deltas against each athlete\u2019s own baseline, each one weighted by predictive power.',
    bullets: [
      '7-day trends, rolling z-scores, chronic:acute ratios',
      'Per-athlete baselines',
      'Signed SHAP contribution on every signal',
    ],
  },
  {
    num: '03', tag: 'INTERACTIONS',
    title: 'Signals compound. So does risk.',
    body: 'Elevated RHR alone is weak. Elevated RHR + suppressed HRV + high load is a recovery deficit, a pattern that shows up 48\u201372 hours before a soft-tissue injury.',
    bullets: [
      'Recovery Deficit \u00b7 Overload Risk \u00b7 Autonomic Stress',
      '\u00d71.1 \u2013 \u00d71.4 multipliers grounded in peer-reviewed literature',
      'Every multiplier traceable, no black box',
    ],
  },
  {
    num: '04', tag: 'INSIGHT',
    title: 'One number. Full decomposition.',
    body: 'Your staff sees a composite injury-risk score first. Every score is one click from the signals and interactions that moved it, so you coach the why, not just the what.',
    bullets: [
      '0 (ready) \u2192 100 (high risk), per athlete, per day',
      'Day-over-day delta, 14-day trend, tier classification',
      'Score \u2192 interaction \u2192 signal \u2192 raw sensor in three clicks',
    ],
  },
];

// ─── Focus targets ─────────────────────────────────────────────────────
// VIEW_W + column center-x values are imported from landing-signal-flow-data
// to keep the SVG renderer and the scroll orchestrator from drifting.
// Only a subtle zoom per stage to emphasize the focused column; the big
// layout change happens via diagram slide, not scale. Hero uses viewport
// center (VIEW_W/2) so no horizontal shift is applied before stages begin.
const STAGE_FOCUS: Array<{ cx: number; scale: number }> = [
  { cx: VIEW_W / 2, scale: 1    },
  { cx: SRC_CX,     scale: 1.30 },
  { cx: SIG_CX,     scale: 1.30 },
  { cx: INT_CX,     scale: 1.30 },
  { cx: INS_CX,     scale: 1.38 },
];

// Scroll budget (in vh) per section. Per-stage pacing is tuned so each
// stage plays an entry (~10%), holds for the middle with content reveal
// (~80%), then exits (~10%). Shorter than the previous 140/100 so the
// transitions don't feel dragged out.
const HERO_VH = 100;
const STAGE_VH_DESKTOP = 55;
const STAGE_VH_MOBILE = 45;

// Narrative + focus transition window fraction of a stage — matches the
// entry and exit animation bounds below.
const FADE = 0.10;

const stageVhFor = (mode: Mode): number =>
  mode === 'mobile' ? STAGE_VH_MOBILE : STAGE_VH_DESKTOP;

// ─── Mode detection ────────────────────────────────────────────────────
// 'static' is reserved for reduced-motion users, the scrolly orchestrator
// is disabled entirely. 'mobile' keeps the scroll-driven stage progression
// but stacks diagram-on-top / narrative-below for narrow viewports.
type Mode = 'full' | 'compact' | 'mobile' | 'static';

function detectMode(): Mode {
  if (typeof window === 'undefined') return 'full';
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 'static';
  const w = window.innerWidth;
  if (w < 640) return 'mobile';
  if (w < 1024) return 'compact';
  return 'full';
}

const easeInOut = (x: number): number =>
  x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;

// ─── Orchestrator ──────────────────────────────────────────────────────
const ScrollyHero: React.FC<ScrollyHeroProps> = () => {
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [t, setT] = useState(0);
  const [mode, setMode] = useState<Mode>(() => detectMode());

  useLayoutEffect(() => {
    const onChange = () => setMode(detectMode());
    window.addEventListener('resize', onChange);
    // Also re-evaluate when the user toggles prefers-reduced-motion
    // mid-session (OS-level low-power modes, accessibility settings).
    const motionMq = window.matchMedia('(prefers-reduced-motion: reduce)');
    motionMq.addEventListener('change', onChange);
    return () => {
      window.removeEventListener('resize', onChange);
      motionMq.removeEventListener('change', onChange);
    };
  }, []);

  useLayoutEffect(() => {
    if (mode === 'static') return;
    const el = wrapRef.current;
    if (!el) return;
    const handle = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const stickyTop = 64;
      const totalScroll = el.offsetHeight - (vh - stickyTop);
      const scrolled = -rect.top;
      if (scrolled <= 0) return setT(0);
      if (scrolled >= totalScroll) return setT(1 + STAGES.length);

      // Convert pixel-scroll to virtual vh and split it explicitly into
      // the hero window (HERO_VH) plus a per-stage window (stageVh). Each
      // stage reads its own `stageVh` budget so swapping STAGE_VH_DESKTOP
      // and STAGE_VH_MOBILE directly re-paces per-stage scroll. If stages
      // ever need non-uniform budgets, replace `stageVh` with a per-index
      // lookup here.
      const stageVh = stageVhFor(mode);
      const totalVh = HERO_VH + STAGES.length * stageVh;
      const scrolledVh = (scrolled / totalScroll) * totalVh;
      if (scrolledVh < HERO_VH) {
        setT(scrolledVh / HERO_VH);
        return;
      }
      const stagesScrolledVh = scrolledVh - HERO_VH;
      const stageIdx0 = Math.min(STAGES.length - 1, Math.floor(stagesScrolledVh / stageVh));
      const withinStage = (stagesScrolledVh - stageIdx0 * stageVh) / stageVh;
      setT(1 + stageIdx0 + withinStage);
    };
    handle();
    window.addEventListener('scroll', handle, { passive: true });
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle);
      window.removeEventListener('resize', handle);
    };
  }, [mode]);

  if (mode === 'static') {
    return <StaticHero navigate={navigate} />;
  }

  // Derived values
  const heroProgress = Math.max(0, Math.min(1, t));
  const inStages = t >= 1;
  const stageFloat = Math.max(0, t - 1);
  const stageIdx = Math.min(STAGES.length, Math.floor(stageFloat) + 1);
  const stageFrac = Math.min(1, Math.max(0, stageFloat - (stageIdx - 1)));
  const focusStage = (inStages ? stageIdx : 0) as FocusStage;

  // Boundary-centered focus interpolation: compute focus as a continuous
  // function of raw `t` so that crossing an integer boundary (e.g., stage
  // 1 → stage 2) doesn't snap the diagram back to the previous column for
  // a frame. The crossfade window is centered on the boundary and straddles
  // the out-fade of stage N (t just below N+1) and the in-fade of stage
  // N+1 (t just above N+1). Outside the window the diagram holds on the
  // current stage's focus.
  //
  //   t in [N + (1-FADE), N + 1]  -> crossfade first half  (frac 0.0 -> 0.5)
  //   t at boundary N + 1         -> crossfade midpoint    (frac 0.5)
  //   t in [N + 1, N + 1 + FADE]  -> crossfade second half (frac 0.5 -> 1.0)
  //   t in [N + FADE, N + 1-FADE] -> stable on FOCUS[N]
  const tNonNeg = Math.max(0, t);
  const floorN = Math.floor(tNonNeg);
  const fracN = tNonNeg - floorN;
  let focusFrom: { cx: number; scale: number };
  let focusTo: { cx: number; scale: number };
  let focusFrac: number;
  if (fracN < FADE && floorN > 0 && floorN <= STAGES.length) {
    // Second half of transition crossing integer boundary `floorN`.
    focusFrom = STAGE_FOCUS[floorN - 1];
    focusTo = STAGE_FOCUS[floorN];
    focusFrac = 0.5 + fracN / (2 * FADE);
  } else if (fracN > 1 - FADE && floorN < STAGES.length) {
    // First half of transition into the upcoming boundary `floorN + 1`.
    focusFrom = STAGE_FOCUS[floorN];
    focusTo = STAGE_FOCUS[floorN + 1];
    focusFrac = (fracN - (1 - FADE)) / (2 * FADE);
  } else {
    // Stable on the current stage's focus. Clamp index so past-end scroll
    // pins on the final stage.
    const idx = Math.min(STAGES.length, floorN);
    focusFrom = STAGE_FOCUS[idx];
    focusTo = STAGE_FOCUS[idx];
    focusFrac = 0;
  }
  const focusEase = easeInOut(Math.min(1, Math.max(0, focusFrac)));
  const cx = focusFrom.cx + (focusTo.cx - focusFrom.cx) * focusEase;
  const rawScale = focusFrom.scale + (focusTo.scale - focusFrom.scale) * focusEase;
  const focus: { cx: number; scale: number } = { cx, scale: rawScale };

  // Entry: stageFrac in [0, FADE]          -> slide up + fade in
  // Hold:  stageFrac in [FADE, 1 - FADE]   -> fully settled (content reveal
  //                                           happens in this band)
  // Exit:  stageFrac in [1 - FADE, 1]      -> fade out + slight lift
  // The last stage never exits — nothing follows, so the insight content
  // stays fully visible through the end of the scroll range instead of
  // fading to 0 at stageFrac=1.
  const isLastStage = stageIdx === STAGES.length;

  const narrativeOpacity = (i: number): number => {
    if (!inStages) return 0;
    const activeIdx = stageIdx - 1;
    if (activeIdx !== i) return 0;
    if (stageFrac < FADE) return easeInOut(stageFrac / FADE);
    if (!isLastStage && stageFrac > 1 - FADE) return easeInOut((1 - stageFrac) / FADE);
    return 1;
  };

  const narrativeTransform = (i: number): string => {
    if (!inStages) return 'translateY(32px) scale(0.97)';
    const activeIdx = stageIdx - 1;
    if (activeIdx !== i) {
      // Non-active stages sit at their entry or exit rest position.
      return activeIdx > i ? 'translateY(-24px) scale(0.98)' : 'translateY(32px) scale(0.97)';
    }
    // Active stage: interpolate through entry -> held -> exit.
    if (stageFrac < FADE) {
      const p = easeInOut(stageFrac / FADE);
      const ty = 32 * (1 - p);
      const sc = 0.97 + 0.03 * p;
      return `translateY(${ty.toFixed(2)}px) scale(${sc.toFixed(3)})`;
    }
    if (!isLastStage && stageFrac > 1 - FADE) {
      const p = easeInOut((stageFrac - (1 - FADE)) / FADE);
      const ty = -24 * p;
      const sc = 1 - 0.02 * p;
      return `translateY(${ty.toFixed(2)}px) scale(${sc.toFixed(3)})`;
    }
    return 'translateY(0) scale(1)';
  };

  const heroCopyOpacity = 1 - Math.min(1, heroProgress * 1.6);
  const heroCopyTransform = `translateY(${-heroProgress * 24}px)`;
  const gridProgress = heroProgress;
  const sectionHeight = `${HERO_VH + STAGES.length * stageVhFor(mode)}vh`;

  return (
    <section
      ref={wrapRef}
      data-screen-label="01 Scrolly Hero"
      style={{ height: sectionHeight }}
    >
      <div
        className="notebook-grid scrolly-sticky-viewport"
        style={{
          position: 'sticky',
          top: 64,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            maxWidth: 1280,
            margin: '0 auto',
            padding: mode === 'compact' ? '0 16px' : '0 24px',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <HeroCopy
            opacity={heroCopyOpacity}
            transform={heroCopyTransform}
            navigate={navigate}
          />

          <NarrativeColumn
            stageIdx={stageIdx}
            stageFrac={stageFrac}
            narrativeOpacity={narrativeOpacity}
            narrativeTransform={narrativeTransform}
            mode={mode}
          />

          <DiagramLayer
            focus={focus}
            focusStage={focusStage}
            gridProgress={gridProgress}
            mode={mode}
          />

          {inStages && <StagePips stageIdx={stageIdx} />}
        </div>
      </div>

    </section>
  );
};

// Subcomponents follow, HeroCopy, NarrativeColumn, DiagramLayer, StagePips, StaticHero.

export default ScrollyHero;

// ─── HeroCopy ──────────────────────────────────────────────────────────
interface HeroCopyProps {
  opacity: number;
  transform: string;
  navigate: (path: string) => void;
}

const HeroCopy: React.FC<HeroCopyProps> = ({ opacity, transform, navigate }) => (
  <div
    style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity,
      transform,
      pointerEvents: opacity > 0.2 ? 'auto' : 'none',
      zIndex: 3,
    }}
  >
    {/* Radial gradient pill, keeps hero text readable over the diagram
        without blocking most of the signal flow behind it. We deliberately
        avoid backdrop-filter here: Safari/iOS (and sometimes Chromium)
        paint the blur as a hard rectangle on first render until a scroll
        triggers layer recomposition, producing a visible "flash" the
        moment the user scrolls. The radial gradient alone gives the same
        visual pill without the paint quirk. */}
    <div
      style={{
        textAlign: 'center',
        padding: '48px 56px',
        maxWidth: 620,
        position: 'relative',
        isolation: 'isolate',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: -20,
          background:
            'radial-gradient(ellipse at center, rgba(10,10,10,0.92) 0%, rgba(10,10,10,0.7) 45%, rgba(10,10,10,0) 80%)',
          zIndex: -1,
          pointerEvents: 'none',
        }}
      />
      <h1 style={{ fontSize: 'clamp(34px, 4.6vw, 60px)', lineHeight: 1.02, fontWeight: 600, marginBottom: 14 }}>
        Connect. Analyze. <span style={{ color: '#3b82f6' }}>Compete.</span>
      </h1>
      <p style={{ fontSize: 15, lineHeight: 1.5, color: '#c0c0c0', maxWidth: 500, margin: '0 auto 22px' }}>
        The intelligence layer between your data and your decisions.
      </p>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <button
          onClick={() => navigate('/contact')}
          style={{
            padding: '14px 0',
            width: 200,
            fontSize: 14,
            fontWeight: 600,
            background: '#fff',
            color: '#000',
            border: '1px solid #fff',
            cursor: 'pointer',
            textAlign: 'center',
          }}
        >
          Request a Demo
        </button>
      </div>
    </div>
  </div>
);

// ─── NarrativeColumn ───────────────────────────────────────────────────
interface NarrativeColumnProps {
  stageIdx: number;
  stageFrac: number;
  narrativeOpacity: (i: number) => number;
  narrativeTransform: (i: number) => string;
  mode: Mode;
}

const NarrativeColumn: React.FC<NarrativeColumnProps> = ({ stageIdx, stageFrac, narrativeOpacity, narrativeTransform, mode }) => {
  const isMobile = mode === 'mobile';
  const isCompact = mode === 'compact';
  // Desktop/compact: flex-centered left column. Mobile: lower half of the
  // sticky viewport, so the diagram above stays unobscured.
  const outerStyle: React.CSSProperties = isMobile
    ? {
        position: 'absolute',
        top: '42%',
        bottom: 88,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 2,
      }
    : {
        // Narrative sits on the LEFT; diagram slides rightward to make
        // room.
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'stretch',
        pointerEvents: 'none',
        zIndex: 2,
      };

  const innerStyle: React.CSSProperties = isMobile
    ? {
        width: '100%',
        padding: '0 16px',
        position: 'relative',
        pointerEvents: 'auto',
        height: '100%',
      }
    : {
        width: isCompact ? '92%' : '42%',
        maxWidth: 520,
        paddingRight: isCompact ? 0 : 40,
        position: 'relative',
        pointerEvents: 'auto',
        height: '100%',
      };

  return (
  <div style={outerStyle}>
    <div style={innerStyle}>
      {STAGES.map((s, i) => (
        <div
          key={s.num}
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            opacity: narrativeOpacity(i),
            transform: narrativeTransform(i),
            transition: 'opacity .35s ease, transform .35s ease',
            pointerEvents: narrativeOpacity(i) > 0.5 ? 'auto' : 'none',
          }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: isMobile ? 10 : 12,
              color: '#3b82f6',
              letterSpacing: '0.2em',
              marginBottom: isMobile ? 8 : 16,
            }}
          >
            {s.num} · {s.tag}
          </div>
          <h2 style={{ fontSize: isMobile ? 22 : 'clamp(28px, 3.2vw, 44px)', lineHeight: 1.1, marginBottom: isMobile ? 10 : 18 }}>
            {s.title}
          </h2>
          {/* Stage-4 mobile hides the body paragraph so the AI-insight tile
              fits above the StagePips row without bleeding. */}
          {!(isMobile && i === 3) && (
            <p style={{ color: '#8a8a8a', fontSize: isMobile ? 13 : 15, lineHeight: 1.55, marginBottom: isMobile ? 10 : 18 }}>
              {s.body}
            </p>
          )}
          {/* Bullets render for stages 1–3 on desktop/compact. Stage 4
              replaces them with the AI-insight tile (see below). Mobile
              hides bullets entirely to save vertical room. */}
          {!isMobile && i !== 3 && (
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6, padding: 0 }}>
              {s.bullets.map((b, bi) => (
                <li
                  key={bi}
                  style={{
                    fontSize: 13,
                    color: '#8a8a8a',
                    paddingLeft: 14,
                    position: 'relative',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 9,
                      width: 6,
                      height: 1,
                      background: '#505050',
                    }}
                  />
                  {b}
                </li>
              ))}
            </ul>
          )}

          {/* Stage-4 AI-insight tile, inlined inside the narrative column
              so it stays in the left region, clear of the signal-flow
              diagram on the right. Renders on every mode. `stageFrac` is
              gated to stage 4 so the tile's phased reveal doesn't animate
              off the wrong stage's progress while stage 4 is inactive. */}
          {i === 3 && (
            <div style={{ marginTop: isMobile ? 6 : 4 }}>
              <LandingInsightNarrative
                stageFrac={stageIdx === 4 ? stageFrac : 0}
                compact={isMobile}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
  );
};

// ─── DiagramLayer ──────────────────────────────────────────────────────
interface DiagramLayerProps {
  // `focus` is the continuously-interpolated zoom/pan target computed by
  // the orchestrator. It's already eased across stage boundaries, so the
  // layer applies it directly, no per-stage re-easing (which used to
  // cause a visible "zoom out → slide → zoom back in" jump at each
  // boundary).
  focus: { cx: number; scale: number };
  focusStage: FocusStage;
  gridProgress: number;
  mode: Mode;
}

const DiagramLayer: React.FC<DiagramLayerProps> = ({
  focus, focusStage, gridProgress, mode,
}) => {
  const isMobile = mode === 'mobile';
  const isCompact = mode === 'compact';
  const scaleCap = isMobile ? 1.35 : isCompact ? 1.40 : focus.scale;
  const effectiveScale = Math.min(focus.scale, scaleCap);
  const tx = (0.5 - focus.cx / VIEW_W) * 100;

  // Hero (t<1): diagram fills the viewport, centered.
  // Stages 1–4 (desktop): diagram keeps its full width but slides RIGHT
  // via translateX so the left portion clears for narrative. No width
  // shrink — avoids the "zoom out then re-zoom" feel. A subtle per-stage
  // scale bump (set in STAGE_FOCUS) does the emphasis.
  // Mobile / compact: no horizontal slide; diagram stays centered and
  // layout is stacked vertically.
  // Desktop width eases 92% -> 80% across hero → stages. Subtle enough to
  // feel like a slide, not a shrink, while guaranteeing the INSIGHT label
  // at the right edge still fits in the viewport after the zoom + slide.
  const diagramWidth = isMobile
    ? `${96 - gridProgress * 2}%`
    : isCompact
      ? `${98 - gridProgress * 3}%`
      : `${92 - gridProgress * 12}%`;

  // Horizontal shift applied to the INNER wrapper. Desktop slides the
  // diagram RIGHT so the left half clears for the narrative column.
  // Tuned so the right edge (INSIGHT column) still fits inside a ~1280
  // content area after slide + 1.15 zoom.
  const innerShiftPct = isMobile || isCompact ? 0 : gridProgress * 22;
  // Vertical lift scales with the current zoom so the top/bottom dead
  // space stays balanced regardless of stage. More zoom -> content grows
  // downward from the origin-top scale -> we lift more to compensate.
  // Factor 50 was fitted against the StagePips-occupied bottom strip and
  // the SVG's top/bottom internal padding so the labels don't encroach
  // on the fixed 64px header above the sticky container.
  const innerLiftPct = isMobile ? 0 : -(effectiveScale - 1) * 50;

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        bottom: isMobile ? '58%' : 0,
        left: 0,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      <div
        style={{
          width: diagramWidth,
          pointerEvents: 'auto',
          position: 'relative',
          transform: `translate(${innerShiftPct}%, ${innerLiftPct}%)`,
          transformOrigin: 'center top',
          willChange: 'transform',
        }}
      >
        <div
          style={{
            // Origin top keeps the SVG's column labels (y≈12) inside the
            // viewBox after scale; otherwise a center-origin scale pushes
            // them above y=0 and they get clipped.
            transform: `translate(${tx}%, 0) scale(${effectiveScale})`,
            transformOrigin: 'center top',
            willChange: 'transform',
          }}
        >
          <LandingSignalFlow focusStage={focusStage} animate={true} />
        </div>

      </div>
    </div>
  );
};

// ─── StagePips ─────────────────────────────────────────────────────────
const StagePips: React.FC<{ stageIdx: number }> = ({ stageIdx }) => (
  <div
    aria-hidden="true"
    style={{
      position: 'absolute',
      bottom: 28,
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      gap: 10,
      alignItems: 'center',
      zIndex: 5,
    }}
  >
    {STAGES.map((s, i) => (
      <div key={s.num} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        <div
          style={{
            width: stageIdx - 1 === i ? 28 : 14,
            height: 2,
            background:
              stageIdx - 1 === i
                ? '#3b82f6'
                : stageIdx - 1 > i
                ? 'rgba(255,255,255,0.35)'
                : 'rgba(255,255,255,0.12)',
            transition: 'all .3s',
          }}
        />
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 9,
            letterSpacing: '0.15em',
            color: stageIdx - 1 === i ? '#e8e8e8' : '#505050',
            transition: 'color .3s',
          }}
        >
          {s.num}
        </span>
      </div>
    ))}
  </div>
);

// ─── Static fallback (reduced-motion) ──────────────────────────────────
interface StaticHeroProps {
  navigate: (path: string) => void;
}

const StaticHero: React.FC<StaticHeroProps> = ({ navigate }) => (
  <section
    className="notebook-grid"
    data-screen-label="01 Static Hero"
    style={{ padding: '80px 16px 40px', background: '#0a0a0a' }}
  >
    <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
      <h1 style={{ fontSize: 'clamp(30px, 7vw, 44px)', lineHeight: 1.05, fontWeight: 600, marginBottom: 14 }}>
        Connect. Analyze. <span style={{ color: '#3b82f6' }}>Compete.</span>
      </h1>
      <p style={{ fontSize: 14, lineHeight: 1.5, color: '#8a8a8a', margin: '0 auto 18px', maxWidth: 480 }}>
        Centralize training, recovery, and readiness data in one platform, delivering predictive insights
        to help coaches anticipate performance.
      </p>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
        <button
          onClick={() => navigate('/contact')}
          style={{ padding: '12px 0', width: 200, fontSize: 14, fontWeight: 600, background: '#fff', color: '#000', border: '1px solid #fff', cursor: 'pointer', textAlign: 'center' }}
        >
          Request a Demo
        </button>
      </div>

      <div style={{ marginBottom: 32 }}>
        {/* StaticHero renders when the user opts into prefers-reduced-motion.
            Disable the SMIL particles / CSS dataPulse in LandingSignalFlow
            so we respect that preference. */}
        <LandingSignalFlow focusStage={0} animate={false} />
      </div>

      {STAGES.map((s) => (
        <div
          key={s.num}
          style={{
            border: '1px solid rgba(255,255,255,0.08)',
            background: '#131313',
            padding: '18px 18px',
            marginBottom: 12,
            textAlign: 'left',
          }}
        >
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#3b82f6', letterSpacing: '0.2em', marginBottom: 10 }}>
            {s.num} · {s.tag}
          </div>
          <h3 style={{ fontSize: 20, lineHeight: 1.15, marginBottom: 8, color: '#fff' }}>{s.title}</h3>
          <p style={{ color: '#8a8a8a', fontSize: 13, lineHeight: 1.6, marginBottom: 10 }}>{s.body}</p>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {s.bullets.map((b, bi) => (
              <li key={bi} style={{ fontSize: 12, color: '#8a8a8a', paddingLeft: 12, position: 'relative', marginBottom: 4 }}>
                <span
                  style={{ position: 'absolute', left: 0, top: 8, width: 6, height: 1, background: '#505050' }}
                />
                {b}
              </li>
            ))}
          </ul>
        </div>
      ))}

      <div style={{ marginTop: 24 }}>
        <LandingInsightNarrative stageFrac={1} />
      </div>
    </div>
  </section>
);
